import React from 'react';

import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import {
    AreaPlot,
    LinePlot,
    areaElementClasses,
} from '@mui/x-charts/LineChart';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';

import { AxisId } from 'ff/debrief/models';
import CustomTooltip from 'ff/debrief/components/charts/tooltip/CustomTooltip';
import Scrubber from 'ff/debrief/components/charts/Scrubber';
import {
    createSeriesConfigure,
    getTimeSeriesAxisConfig,
    getYAxisConfig,
} from 'ff/debrief/components/charts/utils';

import { BlueLinearGradient } from 'ff/debrief/components/charts/defs';
import { axisId } from 'ff/debrief/utils';

interface Props {
    timestamps?: Date[];
    currentTime?: Date;
    currentPoint?: { altitudeFeet: number };
    leftAxisId?: AxisId;
    rightAxisId?: AxisId;
    leftAxisData?: number[];
    rightAxisData?: number[];
    terrainData?: number[];
    tooltipBounds?: React.RefObject<HTMLDivElement>;
}

const GRADIENT_ID = 'altitude-gradient';

const FlightChart = ({
    timestamps,
    currentTime,
    currentPoint,
    leftAxisId,
    rightAxisId,
    leftAxisData,
    rightAxisData,
    tooltipBounds,
    terrainData,
    children,
}: React.PropsWithChildren<Props>) => {
    const altitudeAxes: AxisId[] = [
        axisId.AltitudeAxis,
        axisId.AboveGroundLevelAxis,
    ];
    const isPlottingTerrain =
        leftAxisId === axisId.AltitudeAxis ||
        rightAxisId === axisId.AltitudeAxis;
    const useSharedYAxis =
        altitudeAxes.includes(leftAxisId) && altitudeAxes.includes(rightAxisId);

    const leftYAxisConfig = getYAxisConfig(leftAxisId)(leftAxisData);
    const rightYAxisConfig = getYAxisConfig(rightAxisId)(rightAxisData);

    const leftSeriesConfig = createSeriesConfigure(leftAxisId)(leftAxisData);
    const rightSeriesConfig = createSeriesConfigure(rightAxisId)(rightAxisData);

    const yAxesConfig = useSharedYAxis
        ? [leftYAxisConfig, { id: rightAxisId }] // dummy placeholder
        : [leftYAxisConfig, rightYAxisConfig];

    const seriesConfig = useSharedYAxis
        ? [leftSeriesConfig, { ...rightSeriesConfig, yAxisId: leftAxisId }]
        : [leftSeriesConfig, rightSeriesConfig];

    if (isPlottingTerrain) {
        seriesConfig.push(
            createSeriesConfigure(axisId.TerrainElevationAxis)(terrainData)
        );
    }

    return (
        <ResponsiveChartContainer
            xAxis={[getTimeSeriesAxisConfig(timestamps)]}
            yAxis={yAxesConfig}
            series={seriesConfig}
            sx={{
                userSelect: 'none',
                [`& .${areaElementClasses.root}`]: {
                    [`&.MuiAreaElement-series-${axisId.AltitudeAxis}`]: {
                        fill: `url(#${GRADIENT_ID})`,
                    },
                    [`&.MuiAreaElement-series-${axisId.TerrainElevationAxis}`]:
                        {
                            fill: '#BA7B197f',
                        },
                },
                [`& .MuiLineElement-series-${axisId.TerrainElevationAxis}`]: {
                    stroke: 'transparent',
                },
            }}
            margin={{ left: 8, right: 8 }}
            height={300}
        >
            <defs>
                <BlueLinearGradient id={GRADIENT_ID} />
            </defs>
            <ChartsGrid
                sx={{ '& .MuiChartsGrid-line': { strokeDasharray: '4 6' } }}
                horizontal
            />
            <ChartsYAxis
                position="left"
                axisId={leftAxisId}
                tickLabelStyle={{
                    textAnchor: 'start',
                    paddingBottom: 10,
                }}
                disableLine
                disableTicks
            />
            <ChartsYAxis
                position="right"
                axisId={rightAxisId}
                tickLabelStyle={{
                    textAnchor: 'end',
                    top: 5,
                }}
                disableLine
                disableTicks
            />
            <ChartsXAxis
                position="bottom"
                tickLabelStyle={{ transform: 'translateY(16px)' }}
                disableLine
                disableTicks
            />
            <AreaPlot />
            <LinePlot />
            <CustomTooltip boundaryEl={tooltipBounds} />
            <Scrubber
                currentTime={currentTime}
                currentAltitude={currentPoint.altitudeFeet}
                withAirplane={
                    leftAxisId === 'altitude' || rightAxisId === 'altitude'
                }
            />
            {children}
        </ResponsiveChartContainer>
    );
};

export default FlightChart;
